'use client'

import type {ImageField, LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import {ButtonV2} from 'src/common/ButtonV2'
import {Icon} from 'src/common/Icon'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

export type GridCardProps = {
  title: string
  description: string
  image: ImageField
  hasButton: boolean
  buttonLink: LinkField
}

type BackgroundColor = 'Grey' | 'White'

const BACKGROUND_COLOR_CLASS: Record<BackgroundColor, string> = {
  Grey: 'bg-moss-grey-100',
  White: 'bg-moss-white',
}

export type DefaultGridWithFeatureProps = {
  backgroundColor: BackgroundColor | null
  showFeature: boolean
  featurePosition: 'Top' | 'Bottom'
  featureTitle: string
  featureDescription: string
  featureImage: ImageField
  featureNote: string
  featureButtonLabel: string
  featureButtonLink: LinkField
  pageUid: string
  cards: GridCardProps[]
  paddingTop?: string | null
  paddingBottom?: string | null
}

const GridCard = ({title, description, image, hasButton, buttonLink}: GridCardProps) => {
  const {linkResolver} = useLinkResolver()

  const cardClassName = cn(
    'flex flex-col justify-between gap-6 overflow-hidden rounded-lg border border-moss-black/10 bg-moss-white pt-8',
    {'group transition-colors hover:border-moss-black/20': hasButton},
  )

  const cardContent = (
    <>
      <div className="flex items-start justify-between gap-6 px-8">
        <div className="flex flex-col gap-4">
          {title && <h3 className="font-main text-24 font-medium leading-8 text-moss-black">{title}</h3>}
          {description && <p className="font-main text-18 font-normal leading-6 text-moss-grey-400">{description}</p>}
        </div>
        {hasButton && (
          <span
            aria-hidden="true"
            className="flex size-6 shrink-0 items-center justify-center rounded bg-green-neon transition-colors group-hover:bg-moss-black"
          >
            <Icon name="arrow" className="h-2 w-2.5 text-moss-black transition-colors group-hover:text-green-neon" />
          </span>
        )}
      </div>
      {isFilled.image(image) && (
        <div className="relative aspect-video overflow-hidden">
          <PrismicNextImage
            field={image}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      )}
    </>
  )

  if (hasButton) {
    return (
      <Link href={linkResolver(buttonLink, 'grid-card')} className={cardClassName}>
        {cardContent}
      </Link>
    )
  }

  return <div className={cardClassName}>{cardContent}</div>
}

export const DefaultGridWithFeature = ({
  backgroundColor,
  showFeature,
  featurePosition,
  featureTitle,
  featureDescription,
  featureImage,
  featureNote,
  featureButtonLabel,
  featureButtonLink,
  pageUid,
  cards,
  paddingTop,
  paddingBottom,
}: DefaultGridWithFeatureProps) => {
  const {linkResolver} = useLinkResolver()
  const paddingTopClass = PADDING_TOP_CLASS[paddingTop ?? DEFAULT_PADDING] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING] ?? ''
  const backgroundColorClass = BACKGROUND_COLOR_CLASS[backgroundColor ?? 'Grey']

  const featureButtonHref =
    featureButtonLabel && isFilled.link(featureButtonLink) ? linkResolver(featureButtonLink, 'grid-feature') : undefined

  const isFeatureAtBottom = featurePosition === 'Bottom'

  const featureBlock = showFeature && (
    <div
      className={cn(
        'relative z-10 flex w-full flex-col gap-6 overflow-hidden rounded-lg border border-moss-black/10 bg-moss-white lg:block lg:min-h-[480px]',
        isFeatureAtBottom ? 'mt-6 lg:mt-9' : 'mb-6 lg:mb-9',
      )}
    >
      <div className="relative z-10 flex flex-col gap-4 px-8 pt-8 lg:flex-row lg:items-start lg:gap-8">
        <div className="flex flex-col gap-4">
          {featureTitle && <h2 className="font-main text-24 font-medium leading-8 text-moss-black">{featureTitle}</h2>}
          {featureDescription && (
            <p className="font-main text-18 font-normal leading-6 text-moss-grey-400 lg:max-w-[441px]">
              {featureDescription}
            </p>
          )}
        </div>
        {(featureNote || featureButtonHref) && (
          <div className="flex items-center justify-between gap-4 lg:flex-1 lg:justify-end">
            {featureNote && (
              <span className="font-main text-[11px] uppercase tracking-[0.55px] text-green-dark">{featureNote}</span>
            )}
            {featureButtonHref && (
              <ButtonV2
                href={featureButtonHref}
                variant="primary"
                trackingData={{
                  location: 'grid-feature',
                  dataType: 'primary-button',
                  mktgId: '',
                  pageName: pageUid,
                }}
              >
                {featureButtonLabel}
              </ButtonV2>
            )}
          </div>
        )}
      </div>
      {isFilled.image(featureImage) && (
        <div className="relative h-48 overflow-hidden lg:absolute lg:inset-0 lg:h-auto">
          <PrismicNextImage field={featureImage} fill className="object-cover" />
        </div>
      )}
    </div>
  )

  const cardsBlock = cards.length > 0 && (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-9">
      {cards.map((card, cardIndex) => (
        <GridCard
          key={cardIndex}
          title={card.title}
          description={card.description}
          image={card.image}
          hasButton={card.hasButton}
          buttonLink={card.buttonLink}
        />
      ))}
    </div>
  )

  return (
    <section
      data-slice-type="grid_with_feature"
      data-slice-variation="default"
      className={cn(backgroundColorClass, 'px-5 md:px-10 lg:px-[120px]', paddingTopClass, paddingBottomClass)}
    >
      <div className="mx-auto max-w-container">
        {isFeatureAtBottom ? (
          <>
            {cardsBlock}
            {featureBlock}
          </>
        ) : (
          <>
            {featureBlock}
            {cardsBlock}
          </>
        )}
      </div>
    </section>
  )
}
